"use client";

import { useState } from "react";
import { MapPin, X } from "lucide-react";
import { usePostalCode } from "@/lib/postal-code-context";
import { Button } from "@/components/ui/button";

interface Props {
  deliveryMethods: string[] | null | undefined;
  sellingType?: string | null;
  label?: string;
}

function PostalCodeDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { setPostalCode, postalCode } = usePostalCode();
  const [input, setInput] = useState(postalCode || "");
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  const handleSubmit = () => {
    const err = setPostalCode(input);
    if (err) {
      setError(err);
      return;
    }
    setError(null);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onClose(); }}
      onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-sm w-full p-6 relative"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
          aria-label="Fermer"
        >
          <X className="h-5 w-5" />
        </button>
        <div className="flex items-center gap-2 mb-4">
          <MapPin className="h-5 w-5 text-primary" />
          <h2 className="text-base font-semibold text-gray-900">Votre code postal</h2>
        </div>
        <p className="text-xs text-gray-500 mb-3">
          Pour voir la disponibilité et les modes de livraison près de chez vous.
        </p>
        <input
          type="text"
          inputMode="numeric"
          value={input}
          onChange={(e) => { setInput(e.target.value); setError(null); }}
          onKeyDown={(e) => { if (e.key === "Enter") handleSubmit(); }}
          placeholder="Ex : 75011"
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
          autoFocus
        />
        {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
        <Button
          onClick={handleSubmit}
          className="w-full mt-3 bg-primary hover:bg-primary/90 text-white"
        >
          Valider
        </Button>
      </div>
    </div>
  );
}

function DeliveryMethodIcons({ deliveryMethods, showBuildingSite }: { deliveryMethods: string[]; showBuildingSite: boolean }) {
  return (
    <div className="flex flex-col gap-1.5 mt-3">
      {deliveryMethods.includes("DELIVERY") && (
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-full bg-secondary flex items-center justify-center flex-shrink-0">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8" />
              <path d="M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
            </svg>
          </div>
          <span className="text-xs text-gray-600">Livraison à domicile</span>
        </div>
      )}
      {deliveryMethods.includes("AGENCY") && (
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-full bg-secondary flex items-center justify-center flex-shrink-0">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="m2 7 4.41-4.41A2 2 0 0 1 7.83 2h8.34a2 2 0 0 1 1.42.59L22 7" />
              <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
              <path d="M15 22v-4a2 2 0 0 0-2-2h-2a2 2 0 0 0-2 2v4" />
              <path d="M2 7h20" />
              <path d="M22 7v3a2 2 0 0 1-2 2a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 16 12a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 12 12a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 8 12a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 4 12a2 2 0 0 1-2-2V7" />
            </svg>
          </div>
          <span className="text-xs text-gray-600">Retrait magasin</span>
        </div>
      )}
      {showBuildingSite && deliveryMethods.includes("BUILDING_SITE") && (
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-full bg-secondary flex items-center justify-center flex-shrink-0">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2" />
              <path d="M15 18H9" />
              <path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14" />
              <circle cx="17" cy="18" r="2" />
              <circle cx="7" cy="18" r="2" />
            </svg>
          </div>
          <span className="text-xs text-gray-600">Livraison sur chantier (IDF)</span>
        </div>
      )}
    </div>
  );
}

export function ProductCardButton({ deliveryMethods, sellingType, label }: Props) {
  const defaultLabel = sellingType === "RENT" ? "Louer ce produit" : "Acheter ce produit";
  const displayLabel = label ?? defaultLabel;
  const { hasPostalCode, isIDF } = usePostalCode();
  const [cpDialogOpen, setCpDialogOpen] = useState(false);

  const isBuildingSiteOnly =
    deliveryMethods &&
    deliveryMethods.length > 0 &&
    deliveryMethods.includes("BUILDING_SITE") &&
    !deliveryMethods.includes("DELIVERY") &&
    !deliveryMethods.includes("AGENCY");

  const methods = deliveryMethods && deliveryMethods.length > 0 ? deliveryMethods : null;
  // BUILDING_SITE pastille toujours visible (mention "(IDF)" en permanence)
  const showBuildingSiteIcon = !!deliveryMethods?.includes("BUILDING_SITE");

  if (isBuildingSiteOnly && !hasPostalCode) {
    return (
      <>
        <Button
          size="sm"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setCpDialogOpen(true); }}
          className="w-full bg-secondary hover:bg-secondary/90 text-white text-[11px] px-1"
        >
          <MapPin className="mr-0.5 h-3 w-3 flex-shrink-0" />
          Renseignez votre CP
        </Button>
        {methods && <DeliveryMethodIcons deliveryMethods={methods} showBuildingSite={showBuildingSiteIcon} />}
        <PostalCodeDialog open={cpDialogOpen} onClose={() => setCpDialogOpen(false)} />
      </>
    );
  }

  if (isBuildingSiteOnly && !isIDF) {
    return (
      <>
        <Button
          size="sm"
          className="w-full bg-gray-300 text-gray-500 cursor-not-allowed text-[11px] px-1"
          disabled
        >
          <MapPin className="mr-0.5 h-3 w-3 flex-shrink-0" />
          Indisponible hors IDF
        </Button>
        {methods && <DeliveryMethodIcons deliveryMethods={methods} showBuildingSite={showBuildingSiteIcon} />}
      </>
    );
  }

  return (
    <>
      <Button
        size="sm"
        className="w-full bg-secondary hover:bg-secondary/90 text-white"
      >
        {displayLabel}
      </Button>
      {methods && <DeliveryMethodIcons deliveryMethods={methods} showBuildingSite={showBuildingSiteIcon} />}
    </>
  );
}
